import { useState } from 'react';
import { useMutation } from '@apollo/client/react';
import { toast } from 'sonner';
import {
  Button,
  Dialog,
  DialogClose,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Input,
  Label,
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@staff-app/ui';
import type { AgentDevice } from '@staff-app/shared-types';
import { STAFF_ADD_AGENT_DEVICE } from '@staff-app/graphql-client';
import { useDialogSubmitState } from '@/hooks/useDialogSubmitState';

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

interface AddResult {
  staffAddAgentDevice: AgentDevice[];
}

export function AddAgentDeviceDialog({ open, onOpenChange }: Props) {
  const [deviceId, setDeviceId] = useState('');

  const [addMutation, { loading }] = useMutation<AddResult>(STAFF_ADD_AGENT_DEVICE, {
    refetchQueries: ['staffAgentDevices'],
  });

  const { disabled: submitDisabled, disabledTooltip } = useDialogSubmitState({
    isSubmitting: loading,
    isRequiredFilled: !!deviceId.trim(),
  });

  const handleSubmit = async () => {
    try {
      await addMutation({ variables: { deviceId: deviceId.trim() } });
      toast.success('Device added to your list');
      setDeviceId('');
      onOpenChange(false);
    } catch (err) {
      const msg = (err as { message?: string })?.message ?? 'Unknown error';
      // Backend throws NotFoundException when no device matches the id, and ForbiddenException
      // ("Please pair your testing device to OptiSigns Testing account") when the device
      // belongs to any account other than the OptiSigns Testing account.
      toast.error('Could not add device', { description: msg });
    }
  };

  const resetOnClose = (next: boolean) => {
    if (!next) setDeviceId('');
    onOpenChange(next);
  };

  return (
    <Dialog open={open} onOpenChange={resetOnClose}>
      <DialogContent data-testid="add-agent-device-dialog" className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>{'Add device'}</DialogTitle>
        </DialogHeader>

        <div className="flex flex-col gap-4 py-2">
          <div className="space-y-2">
            <Label htmlFor="agent-device-id">{'Device ID'}</Label>
            <Input
              id="agent-device-id"
              placeholder="e.g. 2id3TYZMxwPkb7r3m"
              value={deviceId}
              onChange={(e) => setDeviceId(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && !submitDisabled) handleSubmit();
              }}
              data-testid="add-agent-device-input"
              autoComplete="off"
              autoFocus
            />
            <p className="text-sm text-muted-foreground">
              {'Paste the device’s Mongo _id. Only devices paired to the OptiSigns Testing account can be added.'}
            </p>
          </div>
        </div>

        <DialogFooter>
          <DialogClose asChild>
            <Button variant="ghost">{'Cancel'}</Button>
          </DialogClose>
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <span tabIndex={submitDisabled ? 0 : -1}>
                  <Button
                    onClick={handleSubmit}
                    disabled={submitDisabled}
                    data-testid="add-agent-device-submit"
                  >
                    {loading ? 'Adding…' : 'Add device'}
                  </Button>
                </span>
              </TooltipTrigger>
              {disabledTooltip ? <TooltipContent>{disabledTooltip}</TooltipContent> : null}
            </Tooltip>
          </TooltipProvider>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
